import { useAssetsList } from "@features/ax/assetRegistry/networks/hooks";
import { getConditionLabel } from "@features/ax/utils";
import { logEvent } from "@shared/analytics/analytics";
import { GenericTable } from "@shared/components/GenericTable";
import { HoverRevealText } from "@shared/components/HoverRevealText";
import { PageHeader } from "@shared/components/PageHeader";
import { useToast } from "@shared/hooks/use-toast";
import { TableAction, TableColumn } from "@shared/types/table";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import { Card, CardContent } from "@shared/ui/card";
import { PAGE_SIZE } from "@shared/utils/constants";
import { Box, Package, Plus } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

const getRiskColor = (score: number): string => {
  if (score <= 30) return "bg-green-100 text-green-800";
  if (score <= 60) return "bg-yellow-100 text-yellow-800";
  return "bg-red-100 text-red-800";
};

interface AssetsTabProps {
  network: any;
}

const AssetsTab: React.FC<AssetsTabProps> = ({ network }) => {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showAssetSearchModal, setShowAssetSearchModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const { toast } = useToast();
  useEffect(() => {
    logEvent("assets_tab_viewed");
  }, []);
  const navigate = useNavigate();
  const filters = useMemo(() => {
    const urlFilters = {
      searchData: "",
      systemNetwork: "",
      location: "",
      conditionScore: "",
      riskScore: "",
      uptime: "",
      networkId: id,
    };

    const queryParam = searchParams.get("search");
    if (queryParam) {
      urlFilters.searchData = decodeURIComponent(queryParam);
    }

    const systemParam = searchParams.get("system_id");
    if (systemParam) {
      urlFilters.systemNetwork = systemParam;
    }

    const locationParam = searchParams.get("location");
    if (locationParam) {
      urlFilters.location = locationParam;
    }

    const conditionMinParam = searchParams.get("condition_score_min");
    const conditionMaxParam = searchParams.get("condition_score_max");

    switch (true) {
      case conditionMinParam === "60" && conditionMaxParam === "79":
        urlFilters.conditionScore = "60-79";
        break;

      case conditionMinParam === "80":
        urlFilters.conditionScore = "80+";
        break;

      case conditionMaxParam === "59":
        urlFilters.conditionScore = "<60";
        break;

      default:
        break;
    }

    const riskMinParam = searchParams.get("risk_score_min");
    const riskMaxParam = searchParams.get("risk_score_max");

    switch (true) {
      case riskMaxParam === "30":
        urlFilters.riskScore = "0-30";
        break;

      case riskMinParam === "31" && riskMaxParam === "60":
        urlFilters.riskScore = "31-60";
        break;

      case riskMinParam === "61":
        urlFilters.riskScore = "61+";
        break;

      default:
        // no match → do nothing or set fallback
        break;
    }

    const uptimeParam = searchParams.get("uptime");
    if (uptimeParam) {
      urlFilters.uptime = uptimeParam;
    }

    return urlFilters;
  }, [searchParams, network.id]);

  const { data } = useAssetsList(filters);

  const totalItems = data?.count || 0;
  const totalPages = Math.ceil(totalItems / PAGE_SIZE);
  const startIndex = (currentPage - 1) * PAGE_SIZE;

  const pagination = useMemo(
    () => ({
      currentPage,
      totalPages,
      totalItems,
      itemsPerPage: PAGE_SIZE,
      startItem: startIndex + 1,
      endItem: Math.min(currentPage * PAGE_SIZE, totalItems),
    }),
    [currentPage, totalPages, totalItems, startIndex]
  );

  const assetsColumns: TableColumn[] = [
    {
      key: "assetNo",
      header: "Asset ID",
      type: "custom",
      headerTooltip: "The unique identifier for the asset.",
    },
    {
      key: "assetName",
      header: "Name",
      type: "custom",
      headerTooltip: "The descriptive name of the asset.",
    },
    {
      key: "systemNetwork",
      header: "System / Network",
      headerTooltip:
        "The parent system or network this asset is a component of.",
      render: (value: number, row: any) => (
        <div className="flex items-center gap-2">
          <span className="font-medium">
            {row.systemName ?? row.networkName ?? "NA"}
          </span>
        </div>
      ),
    },
    {
      key: "location",
      header: "Location",
      type: "custom",
      render: (value) => <HoverRevealText text={value} />,
      headerTooltip: "The specific physical location of the asset.",
    },
    {
      key: "conditionScores",
      header: "Condition Score",
      type: "custom",
      render: (value: number) => (
        <div className="flex items-center gap-2">
          <span className="font-medium">{value}</span>
          <Badge variant="secondary" className={getConditionLabel(value).color}>
            {getConditionLabel(value).label}
          </Badge>
        </div>
      ),
      headerTooltip:
        "The physical health of the asset, based on the most recent inspection. Higher is better.",
    },
    {
      key: "riskScore",
      header: "Risk Score",
      type: "custom",
      render: (value: number, row: any) => (
        <div className="flex items-center gap-2">
          <span className="font-medium">{value}</span>
          <Badge variant="secondary" className={getRiskColor(value)}>
            {row.riskStatus}
          </Badge>
        </div>
      ),
      headerTooltip:
        "The overall risk this asset poses, combining its likelihood of failure with the consequences of that failure.",
    },
  ];

  const assetsActions: TableAction[] = [
    {
      label: "View Details",
      onClick: (row: any) => {
        navigate(`/ax/asset-registry/assets/view/${row.id}`);
        toast({
          title: "Asset Details",
          description: `Viewing details for asset ${row.assetNo}`,
        });
      },
      icon: "Eye",
      variant: "outline",
    },
  ];

  const handleSearch = (value: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set("search", value);
    } else {
      newParams.delete("search");
    }

    newParams.set("page", "1");
    setCurrentPage(1);

    setSearchParams(newParams, { replace: true });
  };

  const handlePageChange = useCallback(
    (newPage: number) => {
      const newParams = new URLSearchParams(searchParams);
      newParams.set("page", newPage.toString());

      setSearchParams(newParams, { replace: true });
      setCurrentPage(newPage);
    },
    [searchParams, setSearchParams]
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
            <Box className="h-5 w-5 text-indigo-600" />
          </div>
          <PageHeader
            title="Assets Management"
            subtitle={`Monitor and manage all assets within ${network.networkName}`}
          />
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => {
              navigate("/ax/asset-registry/assets/create/add");
            }}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Asset
          </Button>
        </div>
      </div>

      <Card>
        <CardContent>
          <GenericTable
            data={data?.results || []}
            columns={assetsColumns}
            searchPlaceholder="Search assets by ID, name..."
            searchDebounceMs={300}
            onSearch={handleSearch}
            rowKey="id"
            actions={assetsActions}
            actionsType="dropdown"
            pagination={pagination}
            onPageChange={handlePageChange}
            showFilters={showFilters}
            onShowFiltersChange={setShowFilters}
            emptyMessage="No assets found for this network"
            emptyIcon={<Package className="h-8 w-8 text-gray-400" />}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default AssetsTab;
